import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import { fetchMeeting } from "../../lib/api"
import { extractErrorMessage, type IEnvelopeResult, unwrapData } from "../../lib/api/errors"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import type { IProps } from "./interfaces"
import { MeetingAccessModalView } from "./MeetingAccessModalView"
import type { IAccessCandidate } from "./MeetingAccessModalView/interfaces"

/**
 * MeetingAccessModal — share this meeting with named org members.
 *
 * Smart wrapper: owns the meeting fetch, candidates query, and share
 * mutation. Renders `MeetingAccessModalView` for the actual UI. Hosts only
 * pass `{ opened, onClose, meetingId }` — no need to thread organizer /
 * share-list state through. Sharing is deliberate (ADR 0056): only what is on
 * `shared_with_emails` grants access, never Teams attendees or speaker names.
 */
export const MeetingAccessModal = ({ opened, onClose, meetingId, onChanged }: IProps): React.JSX.Element => {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null)

  // Lazy: fetch only when the modal opens. Most viewers never open it.
  const meetingQuery = useCompanyQuery({
    key: (org) => queryKeys.meeting(org, meetingId),
    queryFn: (company) => fetchMeeting(company, meetingId),
    enabled: opened,
  })

  // Typed + unwrapped: the untyped Eden result used to be read optimistically
  // (`data?.data?.candidates ?? []`), so ANY failure — 403, 500, transport —
  // silently rendered an empty picker that looked like "no other members".
  // `unwrapData` throws the normalized message, which the view surfaces.
  const candidatesQuery = useCompanyQuery({
    key: (org) => queryKeys.meetingCandidates(org, meetingId),
    queryFn: async (company) => {
      // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
      const meetings = company.meetings as any // eslint-disable-line @typescript-eslint/no-explicit-any
      const res = (await meetings({ id: meetingId })["access-candidates"].get()) as IEnvelopeResult<{
        candidates: IAccessCandidate[]
      }>
      return unwrapData(res)
    },
    enabled: opened,
  })

  const shareMutation = useCompanyMutation({
    mutationFn: async (company, { action, email }: { action: "add" | "remove"; email: string }) => {
      // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
      const res = await (company.meetings as any)({ id: meetingId }).access.patch({ action, email }) // eslint-disable-line @typescript-eslint/no-explicit-any
      // Eden resolves on server errors too — check .error so a failed access change
      // doesn't run onSuccess (false success) and actually surfaces to the user.
      if (res.error) throw new Error(extractErrorMessage(res.error))
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.meetingCandidates(orgId, meetingId) })
      onChanged?.()
    },
    onError: (err) => notifyError(err),
  })

  const meeting = meetingQuery.data
  const organizerEmail = meeting?.organizer_email ?? ""
  const sharedWithEmails: string[] = (() => {
    try {
      const parsed: unknown = JSON.parse(meeting?.shared_with_emails ?? "[]")
      return Array.isArray(parsed) ? parsed.filter((e): e is string => typeof e === "string") : []
    } catch {
      return []
    }
  })()

  const candidates: IAccessCandidate[] = candidatesQuery.data?.candidates ?? []

  return (
    <MeetingAccessModalView
      opened={opened}
      onClose={onClose}
      organizerEmail={organizerEmail}
      sharedWithEmails={sharedWithEmails}
      candidates={candidates}
      isLoading={meetingQuery.isLoading}
      errorMessage={meetingQuery.error ? extractErrorMessage(meetingQuery.error) : null}
      candidatesError={candidatesQuery.error ? extractErrorMessage(candidatesQuery.error) : null}
      isMutating={shareMutation.isPending}
      pendingEmail={shareMutation.variables?.email}
      selectedEmail={selectedEmail}
      onSelectEmail={setSelectedEmail}
      onAdd={(email) => {
        shareMutation.mutate({ action: "add", email })
        setSelectedEmail(null)
      }}
      onRemove={(email) => shareMutation.mutate({ action: "remove", email })}
    />
  )
}
